import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { HStack, VStack } from "../../components/utils";
import BCHeader from "../../shared/BCHeader";
import GrantDetailsStepper from "./GrantDetailsStepper";
import GrantDetailsNavigation from "./GrantDetailsNavigation";
import { getEditGrantDetails } from "../../api/Esop";
import { useGetEditGrantDetails } from "../../queries";
import {
  GrantedBy,
  useGrantDetailsStore,
} from "../../store/useGrantDetailsStore";
import { VestingType } from "../../types/VestingTemplate";
import { StrikePriceType } from "../../types/EsopPlan";
import {
  AllocationMode,
  CostOrGain,
} from "../../store/useAllocationMatrixStore";

function calculateAmountFromOptions(
  optionsGranted: number,
  sharePrice: number,
  grantPrice: number,
  conversionShares: number,
  costOrGain?: CostOrGain | null,
  currentAmount?: number,
  currentOptionsGranted?: number
): number | undefined {
  if (!Number.isFinite(optionsGranted)) return undefined;

  const valuePerOption =
    costOrGain === CostOrGain.GAIN
      ? Math.max(0, sharePrice - grantPrice) * conversionShares
      : sharePrice * conversionShares;

  if (valuePerOption > 0) {
    return parseFloat((optionsGranted * valuePerOption).toFixed(4));
  }

  if (
    Number.isFinite(currentAmount) &&
    Number.isFinite(currentOptionsGranted) &&
    (currentAmount || 0) > 0 &&
    (currentOptionsGranted || 0) > 0
  ) {
    return parseFloat(
      (
        (optionsGranted * (currentAmount || 0)) /
        (currentOptionsGranted || 1)
      ).toFixed(4)
    );
  }

  return undefined;
}

const AddGrantDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, id } = useParams();
  const data = useGrantDetailsStore();
  const selectedEmployeeId = useGrantDetailsStore(
    (state) => state.employee?.id
  );
  const setEmployee = useGrantDetailsStore((state) => state.setEmployee);
  const prefilledEmployee = location.state?.prefilledEmployee;
  const recommendationContext = location.state?.recommendationContext;
  const initialStepFromState = location.state?.initialStep;

  const {
    data: editDetails,
    isFetching,
    refetch,
  } = useGetEditGrantDetails(id ?? "");

  useEffect(() => {
    if (editDetails) {
      data.clear();
      data.setActualVestingDay(editDetails.actualVestingDay);
      data.setAmount(editDetails.amount);
      const conversionRatio = parseFloat(
        (1 / (editDetails.conversionRatio || 1)).toFixed(4)
      );
      data.setConversionRatio(
        `${conversionRatio} ${
          conversionRatio > 1 ? "Options" : "Option"
        } : 1 Share`
      );
      data.setDateOfGrant(editDetails.dateOfGrant);
      data.setEmailsTosend(editDetails.emailsToSend);
      data.setGrantPrice(editDetails.grantPrice);
      data.setGrantedBy(editDetails.grantedBy.toString());
      data.setGrantType(editDetails.grantType);
      data.setMaxSalary(editDetails.maxSalary);
      data.setMinSalary(editDetails.minSalary);
      data.setModelMaxValueOfOptions(editDetails.modelMaxValueOfOptions);
      data.setModelValueOfOptions(editDetails.modelValueOfOptions);
      data.setModellingId(editDetails.modellingId);
      data.setOptionMultiplier(editDetails.optionMultiplier);
      data.setOptionsGranted(editDetails.optionsGranted);
      data.setNote(editDetails?.note || "");
      data.setRemarks(editDetails?.remarks || "");
      data.setTags(editDetails?.tags || []);
      data.setPlan(
        mode === "clone"
          ? {
              ...editDetails.plan,
              optionsReserved:
                editDetails.plan.optionsReserved - editDetails.optionsGranted,
            }
          : editDetails.plan
      );
      data.setSelectedExistingTemplate(
        mode === "clone" ? true : editDetails.selectedExistingTemplate
      );
      data.setSharePrice(editDetails.sharePrice);
      data.setValuationId(editDetails.valuationId);
      data.setValueOfOptions(
        editDetails?.plan?.strikePriceType === StrikePriceType.SHARE_PRICE
          ? editDetails.valueOfOptions * editDetails.plan.conversionShares
          : editDetails.valueOfOptions
      );
      data.setVestingDate(editDetails.vestingDate);
      data.setVestingDateType(editDetails.vestingDateType);
      if (
        !(
          editDetails.vestingTemplate.vestingType === VestingType.MANUAL &&
          mode === "clone"
        )
      )
        data.setVestingTemplate(editDetails.vestingTemplate);
      if (mode === "edit") {
        data.setCustomizedGrantLetter(editDetails.customizedGrantLetter);
        data.setUploadCustomGrantLetter(
          editDetails.generateGrantLetter === undefined
            ? Boolean(editDetails.customizedGrantLetter?.customizedLetter)
            : !editDetails.generateGrantLetter
        );
        data.setEmployee(editDetails.employee);
        data.setGrantId(editDetails.grantId);
        data.setAllocationMatrixId(editDetails.allocationMatrixId || "");
        if (editDetails.matchedRule) {
          data.setMatchedRule(editDetails.matchedRule as any);
          if (
            editDetails.matchedRule.allocationMode ===
            AllocationMode.ABSOLUTE_VALUE
          ) {
            data.setGrantedBy(GrantedBy.AMOUNT);
          } else if (
            editDetails.matchedRule.allocationMode ===
            AllocationMode.FIXED_OPTIONS
          ) {
            data.setGrantedBy(GrantedBy.OPTIONS);
          }
        }
        data.setVestingScheduleData(editDetails.vestingScheduleData);
        data.setManualTemplateName(
          editDetails.vestingTemplate.vestingTemplateName
        );
        data.setManualVestingCliff(editDetails.vestingTemplate.cliffPeriod);
        data.setGrantLetterTemplate(
          editDetails.grantLetterTemplate || undefined
        );
        if (recommendationContext?.proposedChanges) {
          const { proposedChanges } = recommendationContext;
          if (proposedChanges.optionsGranted !== undefined) {
            const recommendedOptions = Number(proposedChanges.optionsGranted);

            const isAmountBasedGrant =
              editDetails.grantedBy === GrantedBy.AMOUNT ||
              editDetails.matchedRule?.allocationMode ===
                AllocationMode.ABSOLUTE_VALUE;

            // eslint-disable-next-line no-console
            console.info("[Grant recommendation] Applying recommendation", {
              grantId: editDetails.grantId,
              recommendedOptions,
              isAmountBasedGrant,
              originalOptions: editDetails.optionsGranted,
              originalAmount: editDetails.amount,
              sharePrice: editDetails.sharePrice,
              grantPrice: editDetails.grantPrice,
              conversionShares: editDetails.plan?.conversionShares || 1,
              allocationMode: editDetails.matchedRule?.allocationMode,
              costOrGain: editDetails.matchedRule?.costOrGain,
            });

            if (isAmountBasedGrant) {
              const recommendedValue = Number(proposedChanges.valueOfOptions);
              const recommendedAmount = Number.isFinite(recommendedValue)
                ? recommendedValue
                : calculateAmountFromOptions(
                    recommendedOptions,
                    editDetails.sharePrice,
                    editDetails.grantPrice || 0,
                    editDetails.plan?.conversionShares || 1,
                    editDetails.matchedRule?.costOrGain,
                    editDetails.amount,
                    editDetails.optionsGranted
                  );
              if (recommendedAmount !== undefined) {
                // eslint-disable-next-line no-console
                console.info(
                  "[Grant recommendation] Calculated amount from recommendation",
                  { grantId: editDetails.grantId, recommendedAmount }
                );
                data.setAmount(recommendedAmount);
              } else {
                // eslint-disable-next-line no-console
                console.warn(
                  "[Grant recommendation] Unable to calculate an amount from the recommendation",
                  {
                    grantId: editDetails.grantId,
                    reason:
                      "Neither the current pricing nor the original amount-per-option is usable",
                  }
                );
              }
            }

            data.setOptionsGranted(recommendedOptions);
          }
        }
      } else if (mode === "clone") {
        navigate("/options/grant/add");
      }
    }
  }, [isFetching]);

  useEffect(() => {
    if (
      mode === "add" &&
      prefilledEmployee &&
      selectedEmployeeId !== prefilledEmployee.id
    ) {
      setEmployee(prefilledEmployee);
    }
  }, [mode, prefilledEmployee, selectedEmployeeId, setEmployee]);

  const [currentStep, setCurrentStep] = useState(
    initialStepFromState ?? (mode === "add" || mode === "clone" ? 1 : 4)
  );

  function onStepChange(step?: number) {
    setCurrentStep(step || parseInt(`${currentStep}`, 10) + 1);
  }
  function onBackClick() {
    if (currentStep !== 1) {
      setCurrentStep(parseInt(`${currentStep}`, 10) - 1);
    }
  }
  return (
    <VStack className="h-full gap-4">
      <div className={`flex items-baseline justify-between`}>
        <BCHeader
          bcTitle={"Equity Plans & Grants"}
          className="py-1"
          bcSubTitle={
            mode === "add"
              ? "Create Grant"
              : mode === "edit"
              ? "Edit Grant"
              : "View Grant"
          }
        />
      </div>
      <HStack className="flex h-full min-h-full gap-4 ">
        <VStack className="flex w-[25%] bg-white rounded-md min-w-72 ">
          <GrantDetailsStepper
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
          />
        </VStack>
        <VStack className="flex w-[75%] bg-white rounded-md min-w-96">
          <GrantDetailsNavigation
            currentStep={currentStep}
            onStepChange={onStepChange}
            onBackClick={onBackClick}
          />
        </VStack>
      </HStack>
    </VStack>
  );
};

export default AddGrantDetails;
